/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { PathLayer } from 'deck.gl';
import { VisionApplication } from '../../application';
import { FormElements } from '../../components/form';
import { Measurement } from '../../components/measurement';
import { TimeSelect } from '../../components/timeselect';
import { VisionLayout } from '../../layout';
import { TrackLine } from '../../map/polyline';
import { AssetService } from '../../service/asset';
import { EventDataResultDTO } from '../../service/event_service';
import { PaginatedFetcher } from '../../service/paginatedFetcher';

import { MowingDTO, MowingService } from './mower_service';
import { MowingMap } from './map';
import { HeatmapDataPoint } from '../../map/heatmap';
import { TelemetryKeysetLoader, TelemetryRow } from '../../service/keyset';
import { buildArrowMarkerSVG, buildMarkerSVG } from '../../map/helpers';

/** The main Mowing Tab for the application.
 *
 */
export class VisionMowingTab {
    // UI Elements
    layout!: dhtmlXLayoutObject;
    assetGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;
    toolbar!: dhtmlXToolbarObject;
    tabbar!: dhtmlXTabBar;

    app: VisionApplication;
    service: MowingService;
    map!: MowingMap;

    count!: number; // Number of assets
    polylineData!: Map<string, TrackLine>; // Asset data map keyed on asset id
    eventLayerData!: Map<string, google.maps.Data>; // Event data map keyed on asset id

    highlightedTrack: TrackLine | undefined; // Current asset selected.
    allSelected: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    statusbar!: any;
    cancelRequest: boolean;
    fetcher: PaginatedFetcher<MowingDTO> | undefined;

    infoWindow!: google.maps.InfoWindow;
    allHeatMapData: HeatmapDataPoint[] = [];
    sliceLoader!: TelemetryKeysetLoader;
    markers: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map<string, google.maps.marker.AdvancedMarkerElement>();

    /** Constructor
     *  TODO: Remove paramter because layout can be found from the Singleton.
     * @param vision The main layout object.
     */
    constructor(vision: VisionLayout) {
        this.app = VisionApplication.getInstance();
        this.service = new MowingService();
        this.polylineData = new Map<string, TrackLine>();
        this.highlightedTrack = undefined;
        this.allSelected = true;
        this.cancelRequest = false;
        this.fetcher = undefined;
        this.eventLayerData = new Map<string, google.maps.Data>();

        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '3J',
        };

        this.layout = vision.tabbar.cells('MOWING').attachLayout(config);

        this.buildUI();
        this.populateGrid();
        this.buildEvents();
    }

    /** Builds all the GUI elements required.
     *
     */
    private buildUI(): void {
        this.layout.cells('c').setText('Mowers');
        this.assetGrid = this.layout.cells('c').attachGrid();
        this.assetGrid.setImagesPath('css/imgs/');
        this.layout.cells('a').hideHeader();

        this.assetGrid.setHeader('Show,Name,Total Miles,Mowed Miles,%', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center;']);
        this.assetGrid.setColSorting('int,int,int,int,int');
        this.assetGrid.setColTypes('ch,ro,ro,ro,ro');
        this.assetGrid.setColAlign('left,center,center,center,center');
        this.assetGrid.init();

        this.toolbar = this.layout.cells('c').attachToolbar();
        //this.toolbar.addButton('new', 0, "Refresh", "css/imgs/new.gif", "");
        this.toolbar.addButtonTwoState('tracks', 1, 'Tracks', '', '');
        this.toolbar.addButtonTwoState('events', 2, 'Events', '', '');
        this.toolbar.addButtonTwoState('heatmap', 3, 'Heatmap', '', '');

        this.toolbar.setItemState('tracks', true);
        this.toolbar.setItemState('events', true);
        this.toolbar.setItemState('heatmap', true);

        this.tabbar = this.layout.cells('b').attachTabbar();

        this.tabbar.addTab('map', 'MAP', 100, 1, true, false);
        //this.tabbar.addTab("dashboard", "Dashboard", 100, 2, false, false);

        this.map = new MowingMap(this);

        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM));
        formData.push({
            type: 'combo',
            name: 'report_format',
            label: 'Report',
            labelWidth: '120',
            offsetLeft: '12',
            width: '200',
            options: [
                { text: '🌐  Web', value: 'web' },
                { text: '📄  PDF', value: 'pdf' },
                { text: '📊  Excel', value: 'excel' },
            ],
        });

        formData.push({
            type: 'select',
            name: 'report_group',
            label: 'Group',
            labelWidth: '120',
            offsetLeft: '12',
            width: '200',
            options: [
                { text: '🚚  Asset', value: 'asset' },
                { text: '📅  Date', value: 'date' },
            ],
            hidden: true,
        });

        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search'), { type: 'newcolumn' }, FormElements.button('cancel', 'Cancel', 'cancel')],
        });

        this.paramForm = this.layout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('cancel');

        this.layout.cells('a').fixSize(false, true);
        this.layout.cells('a').setHeight(300);
        this.layout.cells('a').setWidth(400);
        this.statusbar = this.layout.cells('a').attachStatusBar();
    }

    /** Build all the GUI event handlers
     *
     */
    private buildEvents(): void {
        // Tool bar handlers
        this.toolbar.attachEvent('onStateChange', (itemId, state) => {
            this.toolbarButtonChange(itemId, state);
        });

        // Asset Grid Handlers
        this.assetGrid.attachEvent('onRowSelect', (id) => {
            this.selectMower(id);
        });
        this.assetGrid.attachEvent('onHeaderClick', (id, _obj) => {
            this.headerClicked(id);
        });
        this.assetGrid.attachEvent('onCheck', (rid, _cid, state) => {
            this.rowChecked(rid, state);
        });

        // Form Events
        this.paramForm.attachEvent('onChange', (name, value, state) => {
            this.formChange(name, value, state);
        });

        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'search': {
                    const reportType = this.paramForm.getItemValue('report_format');
                    if (reportType == 'web') {
                        this.clearData();
                        this.paramForm.showItem('cancel');
                        this.getData();
                        this.cancelRequest = false;
                    } else {
                        const reportGroup = this.paramForm.getItemValue('report_group');
                        this.downloadReport(reportType, reportGroup);
                    }

                    break;
                }
                case 'cancel': {
                    if (this.sliceLoader != undefined) {
                        this.sliceLoader.cancel();
                    }
                }
            }
        });
    }

    public formChange(name: string, value: string | number, _state: boolean) {
        if (name == TimeSelect.SELECT) {
            this.populateTime(value);
        }
        if (name == 'report_format') {
            if (value == 'web') {
                this.paramForm.hideItem('report_group');
            } else {
                this.paramForm.showItem('report_group');
            }
        }
    }
    private populateTime(period: string | number): void {
        const select = new TimeSelect(this.paramForm);
        select.selectRange(period as string);
    }

    /** User has selected a mower from the grid list.
     * This will move the map to show the track for that mower.
     *
     * @param id
     */
    private selectMower(id: string): void {
        const track = this.polylineData.get(id);
        if (track) {
            //this.highlightedTrack?.setColour("#FF00FF");
            this.map.map.map.fitBounds(track.bounds);
            //track.setColour("#00fff2");
            this.highlightedTrack = track;
        }
    }

    /** When the users clicks one of the two state buttons in the toolbar
     *
     * @param itemId The name of the button:
     *  * tracks
     * @param newstate The new state of the button
     */
    private toolbarButtonChange(itemId: string, newstate: boolean): void {
        switch (itemId) {
            case 'tracks': {
                this.polylineData.forEach((data) => {
                    data.setVisisble(newstate);
                });
                break;
            }

            case 'events': {
                this.eventLayerData.forEach((layer, id) => {
                    if (newstate == false) {
                        layer.setMap(null);
                    } else {
                        layer.setMap(this.map.map.map);
                    }
                });
                break;
            }

            case 'heatmap': {
                this.map.heatmap.setHeatmapVisible(newstate);
                break;
            }
        }
    }

    private downloadReport(format: string, group: string) {
        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();

        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        if (group == 'asset') {
            if (format == 'pdf') {
                this.service.downloadPDF('/report/mower/summary/asset?format=pdf&start=' + startTime + '&end=' + endTime, 'Mower-Report.pdf');
            } else {
                this.service.downloadXLSX('/report/mower/summary/asset?format=xlsx&start=' + startTime + '&end=' + endTime, 'Mower-Report.xlsx');
            }
        } else if (group == 'date') {
            if (format == 'pdf') {
                this.service.downloadPDF('/report/mower/summary/date?format=pdf&start=' + startTime + '&end=' + endTime, 'Mower-Report.pdf');
            } else {
                this.service.downloadXLSX('/report/mower/summary/date?format=xlsx&start=' + startTime + '&end=' + endTime, 'Mower-Report.xlsx');
            }
        }
    }

    private clearData(): void {
        this.map.heatmap.clearHeatmap();

        this.polylineData.forEach((data, _key) => {
            data.remove();
        });
        this.polylineData.clear();

        this.markers.forEach((marker) => {
            marker.map = null;
        });
        this.markers.clear();
    }

    private async getData(): Promise<void> {
        this.sliceLoader = new TelemetryKeysetLoader({
            orgId: this.app.organizationService.dto?.id ?? '',
            start: String(this.paramForm.getItemValue(TimeSelect.START).getTime()),
            end: String(this.paramForm.getItemValue(TimeSelect.END).getTime()),
            assetType: AssetService.TYPE_MOWER,

            onChunk: (data, page) => {
                this.statusbar.setText('Loading Track Data ' + (page + 1));
                this.loadChunk(data);
            },

            onComplete: () => {
                this.statusbar.setText('Load Complete');
                const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
                const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();

                this.displayHeatmap();
                this.getTableData(startTime, endTime);
                this.getEvents(startTime, endTime);
            },
        });

        await this.sliceLoader.loadAll();
    }

    private loadChunk(data: TelemetryRow[]): void {
        const bounds = new google.maps.LatLngBounds();
        data.forEach((item) => {
            const ext = this.loadTrackData(item);
            if (ext != null) {
                bounds.union(ext);

                const activityOn = this.getMowerInput(item.assetId, item);
                if (activityOn == true) {
                    this.map.heatmap.toggleAsset(item.assetId, true);

                    this.map.heatmap.allPoints.push({
                        location: new google.maps.LatLng(item.latitude, item.longitude),
                        weight: 10,
                        assetId: item.assetId,
                    });

                    this.map.heatmap.heatData.push({
                        location: new google.maps.LatLng(item.latitude, item.longitude),
                        weight: 10,
                        asset: item.assetId,
                    });

                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        map: null,
                        position: { lat: item.latitude, lng: item.longitude },
                        content: buildMarkerSVG(item.heading, 'orange', '12', '12'), // heading in degrees
                    });
                    this.markers.set(Math.random().toString(), marker);
                } else {
                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        map: null,
                        position: { lat: item.latitude, lng: item.longitude },
                        content: buildMarkerSVG(item.heading, 'blue', '12', '12'), // heading in degrees
                    });
                    this.markers.set(Math.random().toString(), marker);
                }
            }
        });

        this.map.heatmap.indexPoints();
        this.map.heatmap.setHeatmapData(this.map.heatmap.heatData, false);
        this.map.heatmap.setHeatmapVisible(true);
    }

    private getEvents(startTime: number, endTime: number) {
        this.eventLayerData.forEach((layer) => {
            layer.setMap(null);
        });
        this.eventLayerData.clear();

        this.infoWindow = new google.maps.InfoWindow({
            content: "Hello, I'm a popup!",
            pixelOffset: new google.maps.Size(8, -32),
        });

        const ids = this.assetGrid.getAllRowIds(',').split(',');
        ids.forEach((i) => {
            const request = this.service.getEvents(i, startTime, endTime);
            request.then((data) => {
                if (data.parsedBody) {
                    const myRouteLayer = new google.maps.Data();
                    myRouteLayer.setMap(this.map.map.map);
                    myRouteLayer.addGeoJson(JSON.parse(data.parsedBody[0].geoJson));
                    myRouteLayer.setStyle({
                        strokeColor: 'green',
                        strokeWeight: 4,
                        zIndex: 9999,
                    });

                    this.eventLayerData.set(i, myRouteLayer);

                    //----------------------------------------------------------------------------------
                    // Display on Mouse Over
                    //----------------------------------------------------------------------------------
                    myRouteLayer.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
                        // highlight the feature
                        myRouteLayer.overrideStyle(event.feature, {
                            strokeWeight: 8,
                            strokeColor: 'cyan',
                            fillOpacity: 0.6,
                        });
                        const feature = event.feature;
                        const assetid = String(feature.getProperty('asset'));
                        const assetName = VisionApplication.getInstance().assetService.assetList.get(assetid);

                        const start = new Date(Number(String(feature.getProperty('start')))).toLocaleDateString('en-us', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                        });
                        const stop = new Date(Number(String(feature.getProperty('stop')))).toLocaleDateString('en-us', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                        });

                        const distance = Measurement.km2miles(Number(feature.getProperty('distance')));
                        const content = `
    <div style="
      min-width: 200px;
      font-family: Arial, sans-serif;
      font-size: 13px;
      line-height: 1.4;
    ">
      <div style="font-weight:bold; font-size:14px; margin-bottom:4px;">${assetName?.id.name}</div>
      <table style="width:100%; border-collapse: collapse;">
        <tr>
          <td style="font-weight:bold; padding:2px 4px; width:70px;">Start:</td>
          <td style="padding:2px 4px;">${start}</td>
        </tr>
        <tr>
          <td style="font-weight:bold; padding:2px 4px;">Stop:</td>
          <td style="padding:2px 4px;">${stop}</td>
        </tr>
        <tr>
          <td style="font-weight:bold; padding:2px 4px;">Distance:</td>
          <td style="padding:2px 4px;">${distance} miles</td>
        </tr>
      </table>
    </div>
  `;

                        this.infoWindow.setContent(content);
                        this.infoWindow.setPosition(event.latLng);
                        this.infoWindow.open({ map: this.map.map.map });
                    });

                    myRouteLayer.addListener('mouseout', (event: google.maps.Data.MouseEvent) => {
                        myRouteLayer.revertStyle(event.feature);
                        this.infoWindow.close();
                    });
                    //----------------------------------------------------------------------------------
                }
            });
        });
    }

    private getTableData(startUTC: number, endUTC: number) {
        this.service.getDailyData(startUTC, endUTC).then((data) => {
            const map = new Map<string, EventDataResultDTO>();
            if (data.parsedBody) {
                data.parsedBody.forEach((item) => {
                    let result;

                    if (map.has(item.id)) {
                        result = map.get(item.id);
                    } else {
                        result = {} as EventDataResultDTO;
                        result.eventKm = 0;
                        result.totalKm = 0;
                    }
                    if (result) {
                        result.eventKm += item.eventKm;
                        result.totalKm += item.totalKm;
                        map.set(item.id, result);
                    }
                });

                map.forEach((data, key) => {
                    const totalMiles = Math.round(Measurement.km2miles(data.totalKm));
                    const totalEventMiles = Math.round(Measurement.km2miles(data.eventKm));

                    this.assetGrid.cells(key, 2).setValue(String(totalMiles));
                    this.assetGrid.cells(key, 3).setValue(String(totalEventMiles));
                    this.assetGrid.cells(key, 4).setValue(String(Math.round((data.eventKm / data.totalKm) * 100)) + ' %');
                });
            }
        });
    }

    /** Get the status of the motorgrader input.
     *
     * @param assetid The id of the asset
     * @param dto The DTO data point from the asset.
     * @returns
     */
    getMowerInput(assetid: string, dto: TelemetryRow): boolean {
        const asset = this.app.assetService.assetList.get(assetid);
        if (asset) {
            if (asset.config.mowingWithSpeed == true) {
                if (dto.speed > 0 && dto.speed < asset.config.mowingWithSpeedKm) {
                    return true;
                } else {
                    return false;
                }
            } else {
                const input = AssetService.findActivity('MOWER', asset.config);
                if (input != undefined) {
                    switch (input) {
                        case 1:
                            return dto.input1 == true;
                        case 2:
                            return dto.input2 == true;
                        case 3:
                            return dto.input3 == true;
                        case 4:
                            return dto.input4 == true;
                        default:
                            return false;
                    }
                } else {
                    return false;
                }
            }
        } else {
            return false;
        }
    }

    private displayHeatmap(): void {
        // Random colour generator.
        const gradientColors = this.generateUniqueColors(this.count);
        let c = 0;

        // Sets the colour of the track line and in the asset grid.
        this.polylineData.forEach((data, key) => {
            const colour = gradientColors[c];
            c = c + 1;
            this.assetGrid.cells(key, 0).setBgColor(colour);
            data.setColour(colour);
        });

        this.map.setData(this.map.heatmap.heatData);
        this.layout.cells('a').progressOff();
    }

    /** Load the track line data.
     * Looks into the cache of tracklines and adds the data point to it.
     *
     * @param data The DTO object from the server with the track lat/lon
     */
    private loadTrackData(data: TelemetryRow): google.maps.LatLngBounds | null {
        if (data.latitude == 0 && data.longitude == 0) {
            return null;
        }

        let trackLine = this.polylineData.get(data.assetId);
        if (trackLine == null) {
            trackLine = new TrackLine();
            trackLine.polyline.addListener('mouseover', () => {
                //alert(data);
            });
        }
        trackLine.showMarkers(false);
        trackLine.addPoint(data.latitude, data.longitude, '#00FF00', 0);
        trackLine.setMap(this.map.map);
        this.polylineData.set(data.assetId, trackLine);

        return trackLine.bounds;
    }

    /** Populate the grid of assets.
     * Uses the chache list of assets to build the list.
     *
     */
    private populateGrid(): void {
        this.assetGrid.clearAll(false);
        this.count = 0;

        this.app.assetService.assetList.forEach((asset) => {
            if (asset.assetType == 'MOWER') {
                this.assetGrid.addRow(asset.id.id, [1, asset.id.name], 0, 0, '', false);
                this.count = this.count + 1;
            }
        });
    }

    /** The header in the asset list was clicked.
     *  This is used to turn on/off all the check boxes.
     *
     * @param id The column index that was clicked ( 0 = the checkbox )
     */
    private headerClicked(id: number) {
        if (id == 0) {
            this.allSelected = !this.allSelected;
            if (this.allSelected == false) {
                this.assetGrid.uncheckAll();
                this.polylineData.forEach((item) => {
                    item.setVisisble(false);
                });
                this.eventLayerData.forEach((layer) => {
                    layer.setMap(null);
                });
                this.map.heatmap.setHeatmapVisible(false);
            } else {
                this.assetGrid.checkAll(true);
                this.polylineData.forEach((item) => {
                    item.setVisisble(true);
                });
                this.eventLayerData.forEach((layer) => {
                    layer.setMap(this.map.map.map);
                });
                this.map.heatmap.setHeatmapVisible(true);
            }
        }
    }

    /** Called when the user checks a row in the asset list.
     *  Will turn the track on/off
     *
     * @param id The id of the row ( ie asset id )
     * @param state The state of the checkbox
     */
    private rowChecked(id: string, state: boolean): void {
        const showingTracks = this.toolbar.getItemState('tracks');
        const showingEvents = this.toolbar.getItemState('events');
        const showingHeatmap = this.toolbar.getItemState('heatmap');

        const grader = this.polylineData.get(id);
        grader?.polyline.setVisible(state && showingTracks);

        const eventLayer = this.eventLayerData.get(id);
        eventLayer?.setMap(state && showingEvents ? this.map.map.map : null);

        this.map.heatmap.toggleAsset(id, state && showingHeatmap);
    }

    /** ChatGPT function that will generate a nice set of colours
     *
     * @param count
     * @returns
     */
    private generateUniqueColors(count: number): string[] {
        // Helper function to convert HSV to RGB
        function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
            const c = v * s;
            const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
            const m = v - c;
            let rgb: [number, number, number] = [0, 0, 0];

            if (h < 60) rgb = [c, x, 0];
            else if (h < 120) rgb = [x, c, 0];
            else if (h < 180) rgb = [0, c, x];
            else if (h < 240) rgb = [0, x, c];
            else if (h < 300) rgb = [x, 0, c];
            else rgb = [c, 0, x];

            return [Math.round((rgb[0] + m) * 255), Math.round((rgb[1] + m) * 255), Math.round((rgb[2] + m) * 255)];
        }

        // Helper function to convert RGB to hex
        function rgbToHex(r: number, g: number, b: number): string {
            return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        }

        const colors: string[] = [];
        const hueStep = 360 / count; // Equal spacing in hue
        const saturation = 0.8; // Strong saturation for vibrant colors
        const value = 0.9; // High brightness for visibility

        for (let i = 0; i < count; i++) {
            const hue = i * hueStep;
            const [r, g, b] = hsvToRgb(hue, saturation, value);
            colors.push(rgbToHex(r, g, b));
        }
        return colors;
    }

    public onSelect(): void {
        this.app.setMapWorkspace(this.map.map.map);
    }

    public mapIdle(): void {
        let zoom = this.map.map.map.getZoom();
        const bounds = this.map.map.map.getBounds();

        if (zoom == undefined) {
            zoom = 15;
        }

        if (zoom > 15) {
            this.markers.forEach((marker) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                if (bounds && bounds.contains(marker.position!)) {
                    marker.map = this.map.map.map;
                } else {
                    marker.map = null;
                }
            });
        } else {
            this.markers.forEach((marker) => {
                marker.map = null;
            });
        }
    }
}
